/**
 * Storefront Toggle Mutations — PricingCard widget
 *
 * Mutation hooks for toggling flags on Pricing Rules.
 * Used by PromotionPill icon buttons and switch.
 *
 * - useToggleShowOnDetail: Eye toggle — which badge to feature on PDP (radio, one per item)
 * - useToggleStorefrontEnabled: Store toggle — which rules apply at checkout (independent)
 * - useTogglePromotionDisabled: on/off switch (enable/disable rule)
 *
 * The PDP PRICE comes from storefront_enabled (same as cart).
 * The Eye toggle only controls the BADGE display.
 *
 * Part of Plan 167 (Storefront Pricing Rules), Epic 03.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { toast } from "sonner";
import {
  pricingSummaryKeys,
  type PricingSummary,
} from "./pricingSummaryHooks";

// ── Toggle "Show on Detail" (Eye — badge display, radio) ────

interface ShowOnDetailInput {
  ruleName: string;
  itemCode: string;
}

interface ShowOnDetailResponse {
  rule_name: string;
  new_value: 0 | 1;
  deactivated: string[];
}

/**
 * Eye toggle — controls which promotion BADGE is featured on the PDP.
 * Radio behavior: only one rule per item can have this ON.
 * Does NOT affect the price — price comes from storefront_enabled rules.
 */
export function useToggleShowOnDetail(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<ShowOnDetailResponse, Error, ShowOnDetailInput, { previous: PricingSummary | undefined }>({
    mutationFn: ({ ruleName, itemCode: ic }) =>
      frappeMethod<ShowOnDetailResponse>(
        "zaviago_backend.api.pricing_storefront_toggles.toggle_storefront_show_on_detail",
        { pricing_rule_name: ruleName, item_code: ic || itemCode }
      ),

    onMutate: async ({ ruleName }) => {
      await queryClient.cancelQueries({
        queryKey: ["pricing-summary", itemCode],
      });

      const currentKey = pricingSummaryKeys.summary(itemCode);
      const previous =
        queryClient.getQueryData<PricingSummary>(currentKey);

      // Optimistic: toggle this rule's show_on_detail, deactivate others (radio)
      if (previous) {
        const toggling = previous.promotions.find((p) => p.name === ruleName);
        const newVal = toggling?.storefront_show_on_detail ? 0 : 1;
        const updated: PricingSummary = {
          ...previous,
          promotions: previous.promotions.map((p) => ({
            ...p,
            storefront_show_on_detail:
              p.name === ruleName
                ? (newVal as 0 | 1)
                : (newVal === 1 ? (0 as const) : p.storefront_show_on_detail),
          })),
        };
        queryClient.setQueryData(currentKey, updated);
      }

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          pricingSummaryKeys.summary(itemCode),
          context.previous
        );
      }
      toast.error("Failed to update badge display");
    },

    onSuccess: (data) => {
      toast.success(
        data.new_value
          ? "Badge shown on product page"
          : "Badge hidden from product page"
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["itemPricingTable", itemCode],
      });
    },
  });
}

// ── Toggle "Enable for Storefront" (Store — checkout pricing, independent) ──

interface StorefrontEnabledInput {
  ruleName: string;
}

interface StorefrontEnabledResponse {
  rule_name: string;
  new_value: 0 | 1;
}

/**
 * Store toggle — controls which rules APPLY at checkout.
 * Independent: multiple rules can be ON simultaneously.
 * This controls the actual PRICE on both PDP and cart.
 */
export function useToggleStorefrontEnabled(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<StorefrontEnabledResponse, Error, StorefrontEnabledInput, { previous: PricingSummary | undefined }>({
    mutationFn: ({ ruleName }) =>
      frappeMethod<StorefrontEnabledResponse>(
        "zaviago_backend.api.pricing_storefront_toggles.toggle_storefront_enabled",
        { pricing_rule_name: ruleName }
      ),

    onMutate: async ({ ruleName }) => {
      await queryClient.cancelQueries({
        queryKey: ["pricing-summary", itemCode],
      });

      const currentKey = pricingSummaryKeys.summary(itemCode);
      const previous =
        queryClient.getQueryData<PricingSummary>(currentKey);

      // Optimistic: toggle this rule's storefront_enabled (no radio — independent)
      if (previous) {
        const toggling = previous.promotions.find((p) => p.name === ruleName);
        const newVal = toggling?.storefront_enabled ? 0 : 1;
        const updated: PricingSummary = {
          ...previous,
          promotions: previous.promotions.map((p) => ({
            ...p,
            storefront_enabled:
              p.name === ruleName
                ? (newVal as 0 | 1)
                : p.storefront_enabled,
          })),
        };
        queryClient.setQueryData(currentKey, updated);
      }

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          pricingSummaryKeys.summary(itemCode),
          context.previous
        );
      }
      toast.error("Failed to update storefront setting");
    },

    onSuccess: (data) => {
      toast.success(
        data.new_value
          ? "Rule enabled for storefront checkout"
          : "Rule disabled for storefront checkout"
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["itemPricingTable", itemCode],
      });
    },
  });
}

// ── Toggle "Promotion Disabled" (on/off switch) ──────────────

interface ToggleDisabledInput {
  ruleName: string;
}

interface ToggleDisabledResponse {
  rule_name: string;
  new_value: 0 | 1;
}

export function useTogglePromotionDisabled(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<ToggleDisabledResponse, Error, ToggleDisabledInput, { previous: PricingSummary | undefined }>({
    mutationFn: ({ ruleName }) =>
      frappeMethod<ToggleDisabledResponse>(
        "zaviago_backend.api.pricing_promotion_actions.toggle_promotion_disabled",
        { pricing_rule_name: ruleName }
      ),

    onMutate: async ({ ruleName }) => {
      await queryClient.cancelQueries({
        queryKey: ["pricing-summary", itemCode],
      });

      const currentKey = pricingSummaryKeys.summary(itemCode);
      const previous =
        queryClient.getQueryData<PricingSummary>(currentKey);

      if (previous) {
        const updated: PricingSummary = {
          ...previous,
          promotions: previous.promotions.map((p) => ({
            ...p,
            disabled:
              p.name === ruleName
                ? (p.disabled ? 0 : 1) as 0 | 1
                : p.disabled,
          })),
        };
        queryClient.setQueryData(currentKey, updated);
      }

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          pricingSummaryKeys.summary(itemCode),
          context.previous
        );
      }
      toast.error("Failed to toggle promotion");
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["itemPricingTable", itemCode],
      });
    },
  });
}
